import { useState } from 'react';
import {
  Modal,
  Upload,
  Typography,
  Alert,
  List,
  Statistic,
  Row,
  Col,
  message,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import type { ImportResult } from '@/types/inventory';

const { Dragger } = Upload;

interface ImportModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onImport: (mdFile: File) => Promise<ImportResult>;
  isPending: boolean;
}

export default function ImportModal({
  open,
  title,
  onClose,
  onImport,
  isPending,
}: ImportModalProps) {
  const [mdFileList, setMdFileList] = useState<UploadFile[]>([]);
  const [mdResult, setMdResult] = useState<ImportResult | null>(null);
  const [mdIsUploading, setMdIsUploading] = useState(false);

  const mdHandleClose = () => {
    setMdFileList([]);
    setMdResult(null);
    onClose();
  };

  const mdHandleImport = async () => {
    if (mdFileList.length === 0) {
      message.warning('Оберіть файл для імпорту');
      return;
    }

    const mdFile = mdFileList[0]?.originFileObj;
    if (!mdFile) {
      message.error('Не вдалося прочитати файл');
      return;
    }

    setMdIsUploading(true);
    try {
      const mdImportResult = await onImport(mdFile);
      setMdResult(mdImportResult);
      if (mdImportResult.errors === 0) {
        message.success(`Імпортовано ${mdImportResult.imported} записів`);
      } else {
        message.warning(`Імпорт завершено з помилками: ${mdImportResult.errors}`);
      }
    } catch {
      message.error('Помилка при імпорті файлу');
    } finally {
      setMdIsUploading(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={mdHandleClose}
      onOk={mdResult ? mdHandleClose : mdHandleImport}
      okText={mdResult ? 'Закрити' : 'Імпортувати'}
      cancelText="Скасувати"
      confirmLoading={isPending || mdIsUploading}
      cancelButtonProps={{ style: mdResult ? { display: 'none' } : undefined }}
      width={560}
    >
      {!mdResult ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Оберіть файл Excel (.xlsx) для імпорту. Перший рядок має містити заголовки колонок.
          </Typography.Paragraph>
          <Dragger
            accept=".xlsx"
            maxCount={1}
            fileList={mdFileList}
            beforeUpload={() => false}
            onChange={(mdInfo) => setMdFileList(mdInfo.fileList)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Натисніть або перетягніть файл сюди
            </p>
            <p className="ant-upload-hint">
              Підтримується тільки формат .xlsx
            </p>
          </Dragger>
        </>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="Імпортовано"
                value={mdResult.imported}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Пропущено"
                value={mdResult.skipped}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Помилки"
                value={mdResult.errors}
                valueStyle={{ color: mdResult.errors > 0 ? '#ff4d4f' : undefined }}
              />
            </Col>
          </Row>

          {mdResult.errorDetails.length > 0 && (
            <Alert
              type="error"
              message="Деталі помилок"
              description={
                <List
                  size="small"
                  dataSource={mdResult.errorDetails}
                  renderItem={(mdItem) => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <Typography.Text type="danger">{mdItem}</Typography.Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 200, overflow: 'auto' }}
                />
              }
              style={{ marginTop: 8 }}
            />
          )}
        </>
      )}
    </Modal>
  );
}
